import { motion } from "framer-motion";
import { B } from "../data/pillars";

function getRiskIndex(avgScore, pillarScores) {
  const sec = pillarScores.secure || 1;
  const ops = pillarScores.operate || 1;
  const mod = pillarScores.modernize || 1;
  const ai = pillarScores.ai || 1;
  const print = pillarScores.print || 1;

  // Weighted composite — security heaviest
  const weighted = (sec * 0.35) + (ops * 0.25) + (mod * 0.20) + (ai * 0.10) + (print * 0.10);

  // Invert to risk: lower OML = higher risk index (0–100)
  const index = Math.round(Math.max(1, Math.min(100, ((5 - weighted) / 4) * 99 + 1)));
  return index;
}

function getRiskBand(index) {
  if (index >= 80) return { label: "CRITICAL", color: B.red, desc: "Severe exposure across multiple technology domains. Immediate remediation required to reduce breach likelihood and operational disruption." };
  if (index >= 60) return { label: "HIGH", color: B.orange, desc: "Significant gaps in security, operations, or infrastructure create meaningful and near-term risk exposure." };
  if (index >= 40) return { label: "ELEVATED", color: B.yellow, desc: "Core controls are partially in place. Targeted improvements will meaningfully reduce exposure and strengthen posture." };
  return { label: "MANAGED", color: B.green, desc: "Technology posture is solid. Focus on continuous improvement, compliance alignment, and strategic optimization." };
}

function getRiskFactors(pillarScores) {
  return [
    { label: "Security Posture", score: pillarScores.secure || 1, weight: "35%" },
    { label: "Operational Resilience", score: pillarScores.operate || 1, weight: "25%" },
    { label: "Infrastructure Modernity", score: pillarScores.modernize || 1, weight: "20%" },
    { label: "AI & Process Maturity", score: pillarScores.ai || 1, weight: "10%" },
    { label: "Document & Print Risk", score: pillarScores.print || 1, weight: "10%" },
  ];
}

export default function RiskExposurePanel({ avgScore, pillarScores, employees }) {
  const index = getRiskIndex(avgScore, pillarScores);
  const band = getRiskBand(index);
  const factors = getRiskFactors(pillarScores);

  const arcAngle = (index / 100) * 180;
  const r = 70;
  const cx = 90, cy = 90;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const startX = cx - r, startY = cy;
  const endAngle = 180 - arcAngle;
  const ex = cx + r * Math.cos(toRad(endAngle));
  const ey = cy - r * Math.sin(toRad(endAngle));

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 16 }}>

        {/* Risk Index Gauge */}
        <div style={{ background: "#1A2E4A", borderRadius: 14, border: `2px solid ${band.color}40`, padding: "24px" }}>
          <div style={{ fontSize: 10, color: B.subText, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 16 }}>
            Composite Risk Index
          </div>

          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
            <svg width={180} height={100} viewBox="0 0 180 100" style={{ overflow: "visible" }}>
              {/* Track */}
              <path
                d={`M ${startX} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
                fill="none" stroke="#1E3A5F" strokeWidth="14" strokeLinecap="round"
              />
              {/* Filled arc */}
              <motion.path
                d={`M ${startX} ${cy} A ${r} ${r} 0 0 1 ${ex} ${ey}`}
                fill="none" stroke={band.color} strokeWidth="14" strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.4, ease: "easeOut" }}
              />
              {/* Score text */}
              <text x={cx} y={cy - 8} textAnchor="middle" fill={band.color} fontSize="32" fontWeight="900" fontFamily="sans-serif">
                {index}
              </text>
              <text x={cx} y={cy + 10} textAnchor="middle" fill={B.subText} fontSize="11" fontFamily="sans-serif">
                OUT OF 100
              </text>
            </svg>
          </div>

          <div style={{ textAlign: "center", marginBottom: 14 }}>
            <div style={{ display: "inline-block", background: band.color + "20", border: `1px solid ${band.color}60`, borderRadius: 6, padding: "4px 14px", color: band.color, fontWeight: 800, fontSize: 13, letterSpacing: 1 }}>
              {band.label} RISK
            </div>
          </div>

          <p style={{ color: B.lightGray, fontSize: 13, lineHeight: 1.6, margin: "0 0 14px", textAlign: "center" }}>
            {band.desc}
          </p>

          <div style={{ background: "#0D1B2E", borderRadius: 8, padding: "10px 14px", fontSize: 11, color: B.subText, lineHeight: 1.6, fontStyle: "italic" }}>
            Full financial risk modeling available through VEIT's <strong style={{ color: B.accent }}>EdgeCheck Technical Audit</strong>.
          </div>
        </div>

        {/* Risk Factor Breakdown */}
        <div style={{ background: "#1A2E4A", borderRadius: 14, border: "1px solid #1E3A5F", padding: "24px" }}>
          <div style={{ fontSize: 10, color: B.subText, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 16 }}>
            Risk Factor Breakdown
          </div>

          {factors.map((f, i) => {
            const contribution = Math.round(((5 - f.score) / 4) * 100);
            const fc = f.score < 2 ? B.red : f.score < 3 ? B.orange : f.score < 4 ? B.yellow : B.green;
            return (
              <div key={f.label} style={{ marginBottom: i < factors.length - 1 ? 14 : 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
                  <div>
                    <span style={{ color: B.white, fontSize: 13, fontWeight: 600 }}>{f.label}</span>
                    <span style={{ color: B.subText, fontSize: 10, marginLeft: 6 }}>({f.weight} weight)</span>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <span style={{ color: fc, fontSize: 11, fontWeight: 700, background: fc + "20", padding: "2px 7px", borderRadius: 4 }}>
                      OML {f.score.toFixed(1)}
                    </span>
                  </div>
                </div>
                <div style={{ background: "#1E3A5F", borderRadius: 6, height: 8, overflow: "hidden" }}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: contribution + "%" }}
                    transition={{ duration: 0.9, delay: i * 0.1, ease: "easeOut" }}
                    style={{ height: "100%", background: `linear-gradient(90deg,${fc}cc,${fc})`, borderRadius: 6 }}
                  />
                </div>
                <div style={{ fontSize: 10, color: B.subText, marginTop: 3 }}>
                  Risk contribution: {contribution}%
                </div>
              </div>
            );
          })}

          <div style={{ marginTop: 18, background: "#122240", borderRadius: 8, padding: "10px 14px", fontSize: 11, color: B.subText, lineHeight: 1.6 }}>
            💡 <em>Index is derived from OML scores across all five pillars using industry-weighted risk factors. It is a relative posture indicator, not an actuarial figure.</em>
          </div>
        </div>

      </div>
    </div>
  );
}